import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth, useUser } from "@clerk/react";
import {
  ShieldCheck, Mail, Loader2, AlertCircle, CheckCircle2,
  Search, BadgeCheck, User, Building2, MapPin, Briefcase,
  Calendar, ArrowRight, ArrowLeft, Sparkles,
} from "lucide-react";
import { LegalFooter } from "@/components/layout/legal-footer";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const REG_BODIES = [
  { key: "nmc", label: "NMC (Nursing)", role: "nurse", hint: "2 digits, letter, 4 digits, letter", eg: "12A3456B" },
  { key: "gmc", label: "GMC (Medicine)", role: "doctor", hint: "7-digit number", eg: "1234567" },
  { key: "gphc", label: "GPhC (Pharmacy)", role: "pharmacist", hint: "7-digit number", eg: "2123456" },
  { key: "hcpc", label: "HCPC (Physio/OT)", role: "physio", hint: "2 letters + 5–6 digits", eg: "PH123456" },
];

const BANDS = ["Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b", "Band 8c", "Band 8d", "Band 9", "Other"];

const FORMATS: Record<string, RegExp> = {
  nmc: /^\d{2}[A-Za-z]\d{4}[A-Za-z]$/,
  gmc: /^\d{7}$/,
  hcpc: /^[A-Za-z]{2}\d{5,6}$/,
  gphc: /^\d{7}$/,
};

export default function OnboardingFlow() {
  const [, setLocation] = useLocation();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { user } = useUser();

  // Step tracking
  const [step, setStep] = useState<"email" | "registration" | "details" | "professional" | "review">("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // NHS Email
  const [workEmail, setWorkEmail] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  // Registration
  const [regBody, setRegBody] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [formatValid, setFormatValid] = useState(false);

  // Personal
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [dob, setDob] = useState("");

  // Professional
  const [orgName, setOrgName] = useState("");
  const [orgOdsCode, setOrgOdsCode] = useState("");
  const [orgQuery, setOrgQuery] = useState("");
  const [orgResults, setOrgResults] = useState<any[]>([]);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [siteName, setSiteName] = useState("");
  const [siteOdsCode, setSiteOdsCode] = useState("");
  const [sites, setSites] = useState<any[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [siteManual, setSiteManual] = useState(false);
  const [band, setBand] = useState("");
  const [jobRole, setJobRole] = useState("");

  // Consents
  const [consentReg, setConsentReg] = useState(false);
  const [consentClinical, setConsentClinical] = useState(false);
  const [consentData, setConsentData] = useState(false);
  const [consentPilot, setConsentPilot] = useState(false);

  useEffect(() => { if (authLoaded && !isSignedIn) setLocation("/"); }, [authLoaded, isSignedIn]);
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setInterval(() => setOtpCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [otpCooldown]);

  const sendOtp = async () => {
    setError(null); setLoading(true);
    setOtpCooldown(60);
    try {
      const res = await fetch(`${basePath}/api/user/send-work-email-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email: workEmail }),
      });
      const data = await res.json();
      if (!res.ok || !data.sent) {
        setError(data.error || "Failed to send code. Please try again.");
        setOtpCooldown(0);
      }
    } catch {
      setError("Network error");
      setOtpCooldown(0);
    }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${basePath}/api/user/verify-work-email-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email: workEmail, otp: emailOtp }),
      });
      const data = await res.json();
      if (res.ok && data.verified) { setEmailVerified(true); setError(null); }
      else { setError(data.error || "Invalid code"); }
    } catch { setError("Network error"); }
    finally { setLoading(false); }
  };

  const checkFormat = (num: string) => {
    const clean = num.replace(/\s/g, "");
    const fmt = FORMATS[regBody];
    setFormatValid(!!fmt && fmt.test(clean));
    setRegNumber(num);
  };

  // ODS organisation search
  const searchOrgs = async (q: string) => {
    setOrgQuery(q);
    if (q.length < 2) { setOrgResults([]); setOrgDropdownOpen(false); return; }
    setOrgLoading(true);
    try {
      const res = await fetch(`${basePath}/api/organisations?q=${encodeURIComponent(q)}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setOrgResults(data.slice(0, 8));
        setOrgDropdownOpen(data.length > 0);
      }
    } catch { /* ignore */ }
    finally { setOrgLoading(false); }
  };

  const selectOrg = (org: any) => {
    setOrgName(org.name);
    setOrgOdsCode(org.odsCode);
    setOrgQuery(org.name);
    setOrgDropdownOpen(false);
    setSiteName("");
    setSiteOdsCode("");
    setSites([]);
    setSiteManual(false);
    setSitesLoading(true);
    fetch(`${basePath}/api/organisations/${org.odsCode}/sites`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => setSites(data))
      .catch(() => setSites([]))
      .finally(() => setSitesLoading(false));
  };

  const selectSite = (site: any) => {
    setSiteName(site.name);
    setSiteOdsCode(site.odsCode);
    setSiteManual(false);
  };

  const canSubmit = emailVerified && formatValid && firstName && lastName && dob && orgName && siteName && band && consentReg && consentClinical && consentData && consentPilot;

  const handleSubmit = async () => {
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${basePath}/api/user/complete-profile`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          firstName, lastName, dateOfBirth: dob,
          workEmail, workEmailOtp: emailOtp,
          registrationBody: regBody, registrationNumber: regNumber,
          organisationName: orgName, organisationOdsCode: orgOdsCode,
          siteName, siteOdsCode: siteOdsCode,
          band, jobRole: jobRole || undefined,
          consents: {
            registrationAccuracy: consentReg,
            clinicalSafety: consentClinical,
            dataProcessing: consentData,
            pilotAcknowledgment: consentPilot,
          },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => {
          if (data.approvalStatus === "approved") setLocation(`/${data.role || "nurse"}`);
          else setLocation("/pending-approval");
        }, 1500);
      } else {
        setError(data.error || "Something went wrong");
      }
    } catch { setError("Network error"); }
    finally { setLoading(false); }
  };

  if (!authLoaded) return null;

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-green-100 text-green-600 mb-6">
              <BadgeCheck className="h-10 w-10" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Registration complete</h1>
            <p className="text-slate-600 mt-2">Setting up your account...</p>
            <Loader2 className="h-6 w-6 animate-spin mx-auto mt-4 text-teal-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-6 py-10">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-teal-50 text-teal-700 mb-3">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Complete your profile</h1>
            <p className="text-slate-500 text-sm mt-1">Step {step === "email" ? "1" : step === "registration" ? "2" : step === "details" ? "3" : step === "professional" ? "4" : "5"} of 5</p>
          </div>

          {/* Progress bar */}
          <div className="flex gap-1 mb-8">
            {["email", "registration", "details", "professional", "review"].map((s, i) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${
                ["email", "registration", "details", "professional", "review"].indexOf(step) >= i ? "bg-teal-500" : "bg-slate-200"
              }`} />
            ))}
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* ── Section 1: NHS Email ── */}
          {step === "email" && (
            <div className="space-y-4 bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-5 w-5 text-teal-600" />
                <h2 className="font-semibold text-slate-900">NHS Email Verification</h2>
              </div>
              {!emailVerified ? (
                <>
                  <input type="email" value={workEmail} onChange={(e) => setWorkEmail(e.target.value)}
                    placeholder="you@nhs.net"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none text-slate-900"
                    disabled={loading} />
                  {!emailOtp && otpCooldown === 0 ? (
                    <button onClick={sendOtp} disabled={loading || !workEmail.includes("@")}
                      className="w-full py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors">
                      {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Send verification code"}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <input type="text" value={emailOtp} onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="000000" maxLength={6}
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 text-center text-2xl tracking-widest outline-none"
                        disabled={loading} />
                      <button onClick={verifyOtp} disabled={loading || emailOtp.length !== 6}
                        className="w-full py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors">
                        {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Verify code"}
                      </button>
                      <div className="text-center">
                        {otpCooldown > 0 ? <p className="text-xs text-slate-400">Resend in {otpCooldown}s</p> :
                          <button onClick={sendOtp} className="text-sm text-teal-600 hover:text-teal-700">Resend code</button>}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <p className="text-sm text-green-700">{workEmail} verified ✓</p>
                </div>
              )}
              {emailVerified && (
                <button onClick={() => setStep("registration")}
                  className="w-full py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors flex items-center justify-center gap-2">
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* ── Section 2: Registration ── */}
          {step === "registration" && (
            <div className="space-y-4 bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <BadgeCheck className="h-5 w-5 text-teal-600" />
                <h2 className="font-semibold text-slate-900">Professional Registration</h2>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {REG_BODIES.map((b) => (
                  <button key={b.key} onClick={() => { setRegBody(b.key); checkFormat(regNumber); }}
                    className={`p-3 rounded-xl border text-left text-sm transition-all ${regBody === b.key ? "border-teal-500 bg-teal-50 ring-2 ring-teal-200" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="font-semibold text-slate-900">{b.label.split(" (")[0]}</div>
                    <div className="text-xs text-slate-500">{b.hint}</div>
                  </button>
                ))}
              </div>
              {regBody && (
                <>
                  <input type="text" value={regNumber}
                    onChange={(e) => checkFormat(e.target.value.toUpperCase())}
                    placeholder={REG_BODIES.find(b => b.key === regBody)?.eg}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 font-mono outline-none" />
                  {regNumber.length >= 4 && (
                    <div className={`p-3 rounded-lg flex items-start gap-2 ${formatValid ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                      {formatValid ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" /> : <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />}
                      <p className={`text-sm ${formatValid ? "text-green-700" : "text-red-700"}`}>
                        {formatValid ? `Valid format — role: ${REG_BODIES.find(b => b.key === regBody)?.role}` : `Invalid format for ${regBody.toUpperCase()}`}
                      </p>
                    </div>
                  )}
                </>
              )}
              <div className="flex gap-3">
                <button onClick={() => setStep("email")} className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button onClick={() => setStep("details")} disabled={!formatValid}
                  className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Section 3: Personal Details ── */}
          {step === "details" && (
            <div className="space-y-4 bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-5 w-5 text-teal-600" />
                <h2 className="font-semibold text-slate-900">Personal Details</h2>
              </div>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name *"
                className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name *"
                className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date of birth *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep("registration")} className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 flex items-center justify-center gap-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button onClick={() => setStep("professional")} disabled={!firstName || !lastName || !dob}
                  className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Section 4: Professional Details ── */}
          {step === "professional" && (
            <div className="space-y-4 bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-5 w-5 text-teal-600" />
                <h2 className="font-semibold text-slate-900">Professional Details</h2>
              </div>

              {/* Employer search */}
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">Employer / NHS Trust *</label>
                <div className="relative">
                  <Search className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                  <input type="text" value={orgName || orgQuery}
                    onChange={(e) => { setOrgName(""); setOrgOdsCode(""); searchOrgs(e.target.value); }}
                    onFocus={() => orgResults.length > 0 && setOrgDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setOrgDropdownOpen(false), 200)}
                    placeholder="Search NHS trust (e.g. Derby)..."
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
                  {orgLoading && <Loader2 className="absolute right-3 top-3.5 h-5 w-5 animate-spin text-slate-400" />}
                </div>
                {orgDropdownOpen && orgResults.length > 0 && (
                  <div className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {orgResults.map((org: any) => (
                      <button key={org.odsCode} type="button"
                        onMouseDown={() => selectOrg(org)}
                        className="w-full text-left px-4 py-3 hover:bg-teal-50 transition-colors border-b border-slate-50 last:border-0">
                        <p className="text-sm font-medium text-slate-800">{org.name}</p>
                        {org.type && <p className="text-xs text-slate-400">{org.type} &middot; {org.odsCode}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Site selection */}
              {orgName && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hospital Site *</label>
                  {sitesLoading ? (
                    <div className="flex items-center gap-2 py-3 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading sites...</div>
                  ) : sites.length > 0 && !siteManual ? (
                    <div className="space-y-1">
                      {sites.map((s: any) => (
                        <button key={s.odsCode} type="button"
                          onClick={() => selectSite(s)}
                          className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${siteOdsCode === s.odsCode ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-teal-300"}`}>
                          <div className="flex items-center gap-2">
                            <MapPin className={`h-4 w-4 shrink-0 ${siteOdsCode === s.odsCode ? "text-teal-600" : "text-slate-400"}`} />
                            <div>
                              <p className="text-sm font-medium text-slate-800">{s.name}</p>
                              {s.city && <p className="text-xs text-slate-400">{s.city}{s.postcode ? ` ${s.postcode}` : ""}</p>}
                            </div>
                          </div>
                        </button>
                      ))}
                      <button type="button"
                        onClick={() => { setSiteManual(true); setSiteName(""); setSiteOdsCode(""); }}
                        className="w-full text-left px-4 py-3 rounded-xl border border-dashed border-slate-300 hover:border-teal-400 transition-colors text-sm text-slate-500 hover:text-teal-600 flex items-center gap-2">
                        <span className="text-lg">+</span> My site isn&apos;t listed — add manually
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                        <input type="text" value={siteName} onChange={(e) => { setSiteName(e.target.value); setSiteOdsCode(""); }}
                          placeholder="e.g. Royal Derby Hospital"
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
                      </div>
                      {sites.length > 0 && (
                        <button type="button"
                          onClick={() => { setSiteManual(false); setSiteName(""); }}
                          className="mt-2 text-xs text-teal-600 hover:text-teal-700">
                          &larr; Back to site list for {orgName}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Band / Grade *</label>
                <select value={band} onChange={(e) => setBand(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 outline-none bg-white">
                  <option value="">Select band...</option>
                  {BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Job Role <span className="text-slate-400 font-normal">(optional)</span></label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input type="text" value={jobRole} onChange={(e) => setJobRole(e.target.value)}
                    placeholder="e.g. Rheumatology Nurse Specialist"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep("details")} className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 flex items-center justify-center gap-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button onClick={() => setStep("review")} disabled={!orgName || !siteName || !band}
                  className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  Review <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Section 5: Review + Legal ── */}
          {step === "review" && (
            <div className="space-y-4">
              {/* Summary card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-teal-600" />
                  <h2 className="font-semibold text-slate-900">Review your details</h2>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Email</span><span className="text-slate-900 font-medium">{workEmail}</span></div>
                  <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Registration</span><span className="text-slate-900 font-medium">{regBody.toUpperCase()}: {regNumber}</span></div>
                  <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Name</span><span className="text-slate-900 font-medium">{firstName} {lastName}</span></div>
                  <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">DOB</span><span className="text-slate-900 font-medium">{dob}</span></div>
                  <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Employer</span><span className="text-slate-900 font-medium">{orgName}</span></div>
                  <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Site</span><span className="text-slate-900 font-medium">{siteName}</span></div>
                  <div className="flex justify-between py-1"><span className="text-slate-500">Band</span><span className="text-slate-900 font-medium">{band}{jobRole ? ` — ${jobRole}` : ""}</span></div>
                </div>
              </div>

              {/* Legal disclaimers */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Before you continue</h2>
                <div className="space-y-4">
                  {[
                    { key: consentReg, set: setConsentReg, label: "I confirm my professional registration details are accurate and belong to me", id: "consent-reg" },
                    { key: consentClinical, set: setConsentClinical, label: "I understand RheumHub is a clinical reference tool and does not replace professional clinical judgment", id: "consent-clinical" },
                    { key: consentData, set: setConsentData, label: "I consent to my data being processed as described in the Privacy Policy", id: "consent-data" },
                    { key: consentPilot, set: setConsentPilot, label: "I acknowledge this is a pilot — features and content may change based on user feedback", id: "consent-pilot" },
                  ].map(({ key, set, label, id }) => (
                    <label key={id} className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={key} onChange={(e) => set(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                      <span className="text-sm text-slate-700">{label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-4">
                  By submitting, you agree to our <a href="/terms" className="text-teal-600 hover:underline" target="_blank">Terms of Use</a> and <a href="/privacy" className="text-teal-600 hover:underline" target="_blank">Privacy Policy</a>.
                </p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep("professional")} className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 flex items-center justify-center gap-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button onClick={handleSubmit} disabled={!canSubmit || loading}
                  className="flex-[2] py-3 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <BadgeCheck className="h-5 w-5" />}
                  Submit & Complete Registration
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
